import React, { useEffect, useState } from "react";
import { Modal, Button, Table, Tabs, Tab } from "react-bootstrap";
import Swal from "sweetalert2";
import { Document, Page, Text, View, StyleSheet, Image, pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';

import Headerimg from "../../../utils/header.jpeg";
import Footerimg from "../../../utils/footer.jpeg";
import { PencilSquare } from "react-bootstrap-icons";

import {
  getEvolucionesByPaciente,
  getMedicacionesbyPaciente,
  getInsumoUsadobyPaciente,
  getSignosVitalesbyPaciente,
} from "../../../api/EvolucionApi";
import {
  updateEvolucion,
  updateMedicacion,
  updateInsumo,
  updateSignosVitales,
} from "../../../api/EvolucionApi";
import { getAdminById, getAdminCredentials } from "../../../api/AdminApi";

/* ===================== COMPONENTE HELPER PARA SALTOS DE LÍNEA ===================== */
const TextoConSaltosDeLinea = ({ texto, style }) => {
  if (!texto) return <Text style={style}>Sin registro</Text>;
  
  // Dividir el texto por saltos de línea
  const lineas = texto.split('\n');
  
  return (
    <>
      {lineas.map((linea, index) => (
        <Text key={index} style={style}>
          {linea || ' '}
        </Text>
      ))}
    </>
  );
};

/* ===================== FUNCIÓN DE LIMPIEZA DE TEXTO - CON SALTOS DE LÍNEA ===================== */

function limpiarTextoParaPDF(text) {
  if (!text) return "";
  
  let limpio = text;
  
  // 1. Reemplazar marcador %bp por salto de línea
  limpio = limpio.replace(/%bp/gi, '\n');
  
  // 2. Eliminar caracteres de control (EXCEPTO \n y \r)
  limpio = limpio.replace(/[\u0000-\u0008\u000B-\u000C\u000E-\u001F\u007F-\u009F]/g, '');
  
  // 3. Normalizar espacios especiales (NO eliminar saltos de línea)
  limpio = limpio.replace(/\u00A0/g, ' ');
  limpio = limpio.replace(/\u2003/g, ' ');
  limpio = limpio.replace(/\u2002/g, ' ');
  limpio = limpio.replace(/\u2009/g, ' ');
  limpio = limpio.replace(/\u202F/g, ' ');
  
  // 4. Reemplazar emojis por círculo ●
  limpio = limpio.replace(/[\u{1F300}-\u{1F9FF}]/gu, '●');
  limpio = limpio.replace(/[\u{2600}-\u{26FF}]/gu, '●');
  limpio = limpio.replace(/[\u{2700}-\u{27BF}]/gu, '●');
  limpio = limpio.replace(/[\u{1F600}-\u{1F64F}]/gu, '●');
  limpio = limpio.replace(/[\u{1F680}-\u{1F6FF}]/gu, '●');
  limpio = limpio.replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '●');
  limpio = limpio.replace(/[\u{1F3FB}-\u{1F3FF}]/gu, '');
  limpio = limpio.replace(/[✓✔✅✗✘❌➕➖➗✖⚠⚡]/g, '●');
  
  // 5. Eliminar caracteres Ï repetidos
  limpio = limpio.replace(/Ï+/g, '');
  limpio = limpio.replace(/[ï]{2,}/g, '');
  
  // 6. Limpiar caracteres especiales problemáticos
  limpio = limpio.replace(/[^\x20-\x7E\u00C0-\u017F\u00A1-\u00BF\n\r]/g, (char) => {
    const code = char.charCodeAt(0);
    if ((code >= 0x00C0 && code <= 0x017F) || 
        (code >= 0x00A1 && code <= 0x00BF)) {
      return char;
    }
    return '';
  });
  
  // 7. Limpiar otros patrones problemáticos
  limpio = limpio.replace(/%[a-z0-9]/gi, '');
  limpio = limpio.replace(/!+/g, '');
  limpio = limpio.replace(/¡+/g, '');
  
  // 8. Normalizar SOLO espacios horizontales múltiples (NO afectar saltos de línea)
  limpio = limpio.replace(/[ \t]{2,}/g, ' ');
  
  // 9. Limpiar espacios al inicio/fin de cada línea (preservar la estructura)
  const lineas = limpio.split('\n');
  limpio = lineas.map(linea => linea.trim()).join('\n');
  
  // 10. Eliminar SOLO líneas completamente vacías múltiples (máximo 2 saltos consecutivos)
  limpio = limpio.replace(/\n{3,}/g, '\n\n');
  
  // 11. Limpiar espacios al inicio y final del texto completo
  return limpio.trim();
}

/* ===================== ESTILOS PARA PDF ===================== */

const styles = StyleSheet.create({
  page: {
    paddingTop: 86,
    paddingBottom: 100,
    paddingLeft: 40,
    paddingRight: 40,
    fontSize: 11,
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff',
  },
  headerImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 90,
  },
  footerImage: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 90,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
    color: '#2c3e50',
  },
  subtitle: {
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 15,
    color: '#34495e',
  },
  separator: {
    borderBottom: '1px solid #95a5a6',
    marginBottom: 15,
  },
  evolucion: {
    marginBottom: 15,
    paddingBottom: 10,
    
    borderBottom: '0.5px solid #ecf0f1',
  },
  encabezado: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 6,
    backgroundColor: '#f0f0f0',
    padding: 6,
    color: '#2c3e50',
  },
  contenido: {
    fontSize: 10,
    color: '#2c3e50',
    marginBottom: 2,
    lineHeight: 1.4,
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 9,
    bottom: 93,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: '#7f8c8d',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#3498db',
    padding: 6,
    color: '#ffffff',
    fontWeight: 'bold',
    fontSize: 9,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '0.5px solid #ecf0f1',
    padding: 6,
    fontSize: 9,
  },
  tableCell: {
    flex: 1,
    paddingRight: 4,
  },
});

/* ===================== COMPONENTES PDF ===================== */

// PDF de Evoluciones
const EvolucionesPDF = ({ evoluciones, nombrePaciente }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <Image src={Headerimg} style={styles.headerImage} fixed />

      <Text style={styles.title}>Evoluciones de Enfermería</Text>
      <Text style={styles.subtitle}>{nombrePaciente}</Text>
      <View style={styles.separator} />

      {evoluciones.map((e, index) => (
        <View key={index} style={styles.evolucion}>
          <Text style={styles.encabezado}>
            {e.fechahora} – {e.profesional}
          </Text>
          <TextoConSaltosDeLinea 
            texto={limpiarTextoParaPDF(e.texto)} 
            style={styles.contenido}
          />
        </View>
      ))}

      <Image src={Footerimg} style={styles.footerImage} fixed />
      
      <Text 
        style={styles.pageNumber} 
        render={({ pageNumber, totalPages }) => (
          `Página ${pageNumber} de ${totalPages}`
        )} 
        fixed 
      />
    </Page>
  </Document>
);

// PDF de Medicaciones
const MedicacionesPDF = ({ medicaciones, nombrePaciente }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <Image src={Headerimg} style={styles.headerImage} fixed />

      <Text style={styles.title}>Medicaciones Administradas</Text>
      <Text style={styles.subtitle}>Paciente: {nombrePaciente}</Text>
      <View style={styles.separator} />

      <View style={styles.tableHeader}>
        <Text style={[styles.tableCell, { flex: 1.5 }]}>Fecha</Text>
        <Text style={[styles.tableCell, { flex: 1.5 }]}>Profesional</Text>
        <Text style={[styles.tableCell, { flex: 2 }]}>Medicamento</Text>
        <Text style={styles.tableCell}>Dosis</Text>
        <Text style={styles.tableCell}>Tipo</Text>
      </View>

      {medicaciones.map((m, index) => (
        <View key={index} style={styles.tableRow}>

          <Text style={[styles.tableCell, { flex: 1.5 }]}>{m.fechaRegistro}</Text>
          <Text style={[styles.tableCell, { flex: 1.5 }]}>{m.Profesional}</Text>
          <Text style={[styles.tableCell, { flex: 2 }]}>
            {limpiarTextoParaPDF(m.Medicamento)}
          </Text>
          <Text style={styles.tableCell}>{m.cantidad} {m.unidadMedida}</Text>
          <Text style={styles.tableCell}>
            {limpiarTextoParaPDF(m.tipo)}
          </Text>
        </View>
      ))}

      <Image src={Footerimg} style={styles.footerImage} fixed />
      <Text 
        style={styles.pageNumber} 
        render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`} 
        fixed 
      />
    </Page>
  </Document>
);

// PDF de Insumos
const InsumosPDF = ({ insumos, nombrePaciente }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <Image src={Headerimg} style={styles.headerImage} fixed />

      <Text style={styles.title}>Insumos Utilizados</Text>
      <Text style={styles.subtitle}>Paciente: {nombrePaciente}</Text>
      <View style={styles.separator} />

      <View style={styles.tableHeader}>
        <Text style={[styles.tableCell, { flex: 1.5 }]}>Fecha</Text>
        <Text style={[styles.tableCell, { flex: 1.5 }]}>Profesional</Text>
        <Text style={[styles.tableCell, { flex: 1.5 }]}>Insumo</Text>
        <Text style={styles.tableCell}>Tipo</Text>
        <Text style={[styles.tableCell, { flex: 0.7 }]}>Cant.</Text>
        <Text style={[styles.tableCell, { flex: 1.5 }]}>Observación</Text>
      </View>

      {insumos.map((i, index) => (
       <View key={index} style={styles.tableRow}>

          <Text style={[styles.tableCell, { flex: 1.5 }]}>{i.fechahora}</Text>
          <Text style={[styles.tableCell, { flex: 1.5 }]}>{i.profesional}</Text>
          <Text style={[styles.tableCell, { flex: 1.5 }]}>
            {limpiarTextoParaPDF(i.insumo)}
          </Text>
          <Text style={styles.tableCell}>
            {limpiarTextoParaPDF(i.tipoinsumo)}
          </Text>
          <Text style={[styles.tableCell, { flex: 0.7 }]}>{i.cantidad}</Text>
          <Text style={[styles.tableCell, { flex: 1.5 }]}>
            {limpiarTextoParaPDF(i.obs) || '-'}
          </Text>
        </View>
      ))}

      <Image src={Footerimg} style={styles.footerImage} fixed />
      <Text 
        style={styles.pageNumber} 
        render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`} 
        fixed 
      />
    </Page>
  </Document>
);

// PDF de Signos Vitales
const SignosVitalesPDF = ({ signos, nombrePaciente }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <Image src={Headerimg} style={styles.headerImage} fixed />

      <Text style={styles.title}>Signos Vitales</Text>
      <Text style={styles.subtitle}>{nombrePaciente}</Text>
      <View style={styles.separator} />

      <View style={styles.tableHeader}>
        <Text style={[styles.tableCell, { flex: 1.5 }]}>Fecha</Text>
        <Text style={[styles.tableCell, { flex: 1.5 }]}>Profesional</Text>
        <Text style={[styles.tableCell, { flex: 0.8 }]}>Temp °C</Text>
        <Text style={[styles.tableCell, { flex: 0.8 }]}>PA</Text>
        <Text style={[styles.tableCell, { flex: 0.6 }]}>FC</Text>
        <Text style={[styles.tableCell, { flex: 0.6 }]}>FR</Text>
        <Text style={[styles.tableCell, { flex: 0.6 }]}>SatO₂</Text>
        <Text style={[styles.tableCell, { flex: 0.6 }]}>HGT</Text>
      </View>

      {signos.map((s, index) => (
        <View key={index} style={styles.tableRow}>
          <Text style={[styles.tableCell, { flex: 1.5 }]}>{s.fechahora}</Text>
          <Text style={[styles.tableCell, { flex: 1.5 }]}>{s.profesional}</Text>
          <Text style={[styles.tableCell, { flex: 0.8 }]}>{s.temperatura}</Text>
          <Text style={[styles.tableCell, { flex: 0.8 }]}>{s.pa}</Text>
          <Text style={[styles.tableCell, { flex: 0.6 }]}>{s.fc}</Text>
          <Text style={[styles.tableCell, { flex: 0.6 }]}>{s.fr}</Text>
          <Text style={[styles.tableCell, { flex: 0.6 }]}>{s.sato2}</Text>
          <Text style={[styles.tableCell, { flex: 0.6 }]}>{s.hgt || '-'}</Text>
        </View>
      ))}

      <Image src={Footerimg} style={styles.footerImage} fixed />
      <Text 
        style={styles.pageNumber} 
        render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`} 
        fixed 
      />
    </Page>
  </Document>
);

/* ===================== FUNCIONES PARA GENERAR Y DESCARGAR PDFs ===================== */

const generarPDFEvoluciones = async (evoluciones, nombrePaciente) => {
  try {
    Swal.fire({
      title: "Generando PDF...",
      text: "Por favor espere",
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    const blob = await pdf(
      <EvolucionesPDF evoluciones={evoluciones} nombrePaciente={nombrePaciente} />
    ).toBlob();
    
    saveAs(blob, `Evoluciones_${nombrePaciente}.pdf`);

    Swal.close();
    Swal.fire({
      icon: "success",
      title: "PDF generado correctamente",
      timer: 1500,
      showConfirmButton: false,
    });
  } catch (error) {
    console.error(error);
    Swal.fire("Error", "No se pudo generar el PDF", "error");
  }
};

const generarPDFMedicaciones = async (medicaciones, nombrePaciente) => {
  try {
    Swal.fire({
      title: "Generando PDF...",
      text: "Por favor espere",
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    const blob = await pdf(
      <MedicacionesPDF medicaciones={medicaciones} nombrePaciente={nombrePaciente} />
    ).toBlob();
    
    saveAs(blob, `Medicaciones_${nombrePaciente}.pdf`);

    Swal.close();
    Swal.fire({
      icon: "success",
      title: "PDF generado correctamente",
      timer: 1500,
      showConfirmButton: false,
    });
  } catch (error) {
    console.error(error);
    Swal.fire("Error", "No se pudo generar el PDF", "error");
  }
};

const generarPDFInsumos = async (insumos, nombrePaciente) => {
  try {
    Swal.fire({
      title: "Generando PDF...",
      text: "Por favor espere",
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    const blob = await pdf(
      <InsumosPDF insumos={insumos} nombrePaciente={nombrePaciente} />
    ).toBlob();
    
    saveAs(blob, `Insumos_${nombrePaciente}.pdf`);

    Swal.close();
    Swal.fire({
      icon: "success",
      title: "PDF generado correctamente",
      timer: 1500,
      showConfirmButton: false,
    });
  } catch (error) {
    console.error(error);
    Swal.fire("Error", "No se pudo generar el PDF", "error");
  }
};

const generarPDFSignos = async (signos, nombrePaciente) => {
  try {
    Swal.fire({
      title: "Generando PDF...",
      text: "Por favor espere",
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    const blob = await pdf(
      <SignosVitalesPDF signos={signos} nombrePaciente={nombrePaciente} />
    ).toBlob();
    
    saveAs(blob, `SignosVitales_${nombrePaciente}.pdf`);

    Swal.close();
    Swal.fire({
      icon: "success",
      title: "PDF generado correctamente",
      timer: 1500,
      showConfirmButton: false,
    });
  } catch (error) {
    console.error(error);
    Swal.fire("Error", "No se pudo generar el PDF", "error");
  }
};

/* ===================== FILTROS ===================== */

function filtrarPorFecha(array, campoFecha, desde, hasta) {
  if (!desde && !hasta) return array;

  return array.filter((item) => {
    if (!item[campoFecha]) return false;

    const fechaItem = new Date(item[campoFecha].split(" ")[0]);
    const fDesde = desde ? new Date(desde) : null;
    const fHasta = hasta ? new Date(hasta) : null;

    if (fDesde && fechaItem < fDesde) return false;
    if (fHasta && fechaItem > fHasta) return false;

    return true;
  });
}

function filtrarPorProfesional(array, campo, texto) {
  if (!texto) return array;

  return array.filter((item) =>
    (item[campo] || "").toLowerCase().includes(texto.toLowerCase())
  );
}

/* ===================== COMPONENTE PRINCIPAL ===================== */

const ModalHojaEnfermeria = ({
  estado,
  onCloseModal,
  idpaciente,
  nombrePaciente,
}) => {
  const [evoluciones, setEvoluciones] = useState([]);
  const [medicaciones, setMedicaciones] = useState([]);
  const [insumos, setInsumos] = useState([]);
  const [signos, setSignos] = useState([]);

  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [filtroProfesional, setFiltroProfesional] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [registroEditando, setRegistroEditando] = useState(null);
  const [tipoRegistro, setTipoRegistro] = useState("");
  const [puedeEditar, setPuedeEditar] = useState(false);

const guardarEdicion = async () => {
  try {
    // Verificar permisos en tiempo real desde la base de datos
    Swal.fire({
      title: "Verificando permisos...",
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    const admindata = await getAdminCredentials();
    const acceso = await getAdminById(admindata.IdUsuario);
    
    if (acceso[0].editarenfermeria !== "true") {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Acceso denegado",
        text: "No tienes permisos para editar registros de enfermería",
      });
      setShowEditModal(false);
      return;
    }

    Swal.fire({
      title: "Actualizando...",
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    if (tipoRegistro === "evolucion") {
      await updateEvolucion(registroEditando.idevolucion, {
        texto: registroEditando.texto,
      });

      setEvoluciones((prev) =>
        prev.map((e) =>
          e.idevolucion === registroEditando.idevolucion ? registroEditando : e
        )
      );
    }

    if (tipoRegistro === "medicacion") {
      await updateMedicacion(registroEditando.idmedicacion, registroEditando);
      setMedicaciones((prev) =>
        prev.map((m) =>
          m.idmedicacion === registroEditando.idmedicacion ? registroEditando : m
        )
      );
    }

    if (tipoRegistro === "insumo") {
      await updateInsumo(registroEditando.idinsumo, registroEditando);
      setInsumos((prev) =>
        prev.map((i) =>
          i.idinsumo === registroEditando.idinsumo ? registroEditando : i
        )
      );
    }

    if (tipoRegistro === "signos") {
      await updateSignosVitales(registroEditando.id, registroEditando);
      setSignos((prev) =>
        prev.map((s) =>
          s.id === registroEditando.id ? registroEditando : s
        )
      );
    }

    Swal.close();
    setShowEditModal(false);
    
    Swal.fire({
      icon: "success",
      title: "Actualizado correctamente",
      timer: 1500,
      showConfirmButton: false,
    });
  } catch (err) {
    console.error("Error al actualizar:", err);
    Swal.close();
    Swal.fire({
      icon: "error",
      title: "Error",
      text: err.message || "No se pudo actualizar el registro",
    });
  }
};

  useEffect(() => {
    if (!estado) return;

    const fetchData = async () => {
      try {
        Swal.fire({
          title: "Cargando Hoja de Enfermería...",
          allowOutsideClick: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        const [ev, med, ins, sig] = await Promise.all([
          getEvolucionesByPaciente(idpaciente),
          getMedicacionesbyPaciente(idpaciente),
          getInsumoUsadobyPaciente(idpaciente),
          getSignosVitalesbyPaciente(idpaciente),
        ]);

        const admindata = await getAdminCredentials();
        const acceso = await getAdminById(admindata.IdUsuario);
        
      
        
        // Verificar si el usuario puede editar
        setPuedeEditar(acceso[0].editarenfermeria === "true");
        
        setEvoluciones(ev);
        setMedicaciones(med);
        setInsumos(ins);
        setSignos(sig);

        Swal.close();
      } catch (error) {
        console.error("Error cargando datos:", error);
        Swal.fire("Error", "No se pudo cargar la hoja", "error");
      }
    };

    fetchData();
  }, [estado, idpaciente]);

  /* ===================== ARRAYS FILTRADOS ===================== */
  
  const evolucionesFiltradas = filtrarPorProfesional(
    filtrarPorFecha(evoluciones, "fechahora", fechaDesde, fechaHasta),
    "profesional",
    filtroProfesional
  );

  const medicacionesFiltradas = filtrarPorProfesional(
    filtrarPorFecha(medicaciones, "fechaRegistro", fechaDesde, fechaHasta),
    "Profesional",
    filtroProfesional
  );

  const insumosFiltrados = filtrarPorProfesional(
    filtrarPorFecha(insumos, "fechahora", fechaDesde, fechaHasta),
    "profesional",
    filtroProfesional
  );

  const signosFiltrados = filtrarPorProfesional(
    filtrarPorFecha(signos, "fechahora", fechaDesde, fechaHasta),
    "profesional",
    filtroProfesional
  );

  return (
    <>
      <Modal show={estado} onHide={onCloseModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Hoja de Enfermería - {nombrePaciente}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* BOTONES PDF */}
          <div className="d-flex gap-2 mb-3 flex-wrap">
            <Button 
              variant="primary"
              onClick={() => generarPDFEvoluciones(evolucionesFiltradas, nombrePaciente)}
            >
              📄 Descargar Evoluciones
            </Button>
            <Button 
              variant="info"
              onClick={() => generarPDFMedicaciones(medicacionesFiltradas, nombrePaciente)}
            >
              💊 Descargar Medicaciones
            </Button>
            <Button 
              variant="success"
              onClick={() => generarPDFInsumos(insumosFiltrados, nombrePaciente)}
            >
              📦 Descargar Insumos
            </Button>
            <Button 
              variant="danger"
              onClick={() => generarPDFSignos(signosFiltrados, nombrePaciente)}
            >
              ❤️ Descargar Signos Vitales
            </Button>
          </div>

          {/* FILTROS */}
          <div className="d-flex gap-3 mb-3 flex-wrap align-items-end">
            <div>
              <label className="form-label">Desde</label>
              <input 
                type="date" 
                className="form-control" 
                value={fechaDesde} 
                onChange={(e) => setFechaDesde(e.target.value)} 
              />
            </div>

            <div>
              <label className="form-label">Hasta</label>
              <input 
                type="date" 
                className="form-control" 
                value={fechaHasta} 
                onChange={(e) => setFechaHasta(e.target.value)} 
              />
            </div>

            <div>
              <label className="form-label">Profesional</label>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por nombre..."
                value={filtroProfesional}
                onChange={(e) => setFiltroProfesional(e.target.value)}
              />
            </div>

            <Button
              variant="outline-danger"
              onClick={() => {
                setFechaDesde("");
                setFechaHasta("");
                setFiltroProfesional("");
              }}
            >
              🗑️ Limpiar filtros
            </Button>
          </div>

          <Tabs defaultActiveKey="evoluciones" className="mb-3">
            {/* TAB EVOLUCIONES */}
            <Tab eventKey="evoluciones" title={`Evoluciones (${evolucionesFiltradas.length})`}>
              {evolucionesFiltradas.length === 0 ? (
                <div className="alert alert-info">No hay evoluciones para mostrar</div>
              ) : (
                <Table bordered hover responsive>
                  <tbody>
                    {evolucionesFiltradas.map((e, i) => (
                      <tr key={i}>
                        <td style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          <div className="d-flex justify-content-between gap-3">
                            <div style={{ flex: 1 }}>
                              <strong style={{ color: "#2c3e50" }}>
                                {e.fechahora} – {e.profesional}
                              </strong>
                              <div style={{ marginTop: "8px", lineHeight: "1.6" }}>
                                {e.texto}
                              </div>
                            </div>

                            {puedeEditar && (
                              <Button
                                size="sm"
                                variant="warning"
                                onClick={() => {
                                  setRegistroEditando(e);
                                  setTipoRegistro("evolucion");
                                  setShowEditModal(true);
                                }}
                                title="Editar evolución"
                              >
                                <PencilSquare size={18} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Tab>

            {/* TAB MEDICACIONES */}
            <Tab eventKey="medicaciones" title={`Medicaciones (${medicacionesFiltradas.length})`}>
              {medicacionesFiltradas.length === 0 ? (
                <div className="alert alert-info">No hay medicaciones para mostrar</div>
              ) : (
                <Table bordered hover responsive>
                  <tbody>
                    {medicacionesFiltradas.map((m, i) => (
                      <tr key={i}>
                        <td>
                          <div className="d-flex justify-content-between gap-3">
                            <div style={{ flex: 1 }}>
                              <strong style={{ color: "#2980b9" }}>
                                {m.fechaRegistro} – {m.Profesional}
                              </strong>
                              <div style={{ marginTop: "8px" }}>
                                <div>💊 Medicamento: <strong>{m.Medicamento}</strong></div>
                                <div>📏 Dosis: {m.cantidad} {m.unidadMedida}</div>
                                <div>📋 Tipo: {m.tipo}</div>
                              </div>
                            </div>

                            {puedeEditar && (
                              <Button
                                size="sm"
                                variant="warning"
                                onClick={() => {
                                  setRegistroEditando(m);
                                  setTipoRegistro("medicacion");
                                  setShowEditModal(true);
                                }}
                                title="Editar medicación"
                              >
                                <PencilSquare size={18} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Tab>

            {/* TAB INSUMOS */}
            <Tab eventKey="insumos" title={`Insumos (${insumosFiltrados.length})`}>
              {insumosFiltrados.length === 0 ? (
                <div className="alert alert-info">No hay insumos para mostrar</div>
              ) : (
                <Table bordered hover responsive>
                  <tbody>
                    {insumosFiltrados.map((i, k) => (
                      <tr key={k}>
                        <td>
                          <div className="d-flex justify-content-between gap-3">
                            <div style={{ flex: 1 }}>
                              <strong style={{ color: "#27ae60" }}>
                                {i.fechahora} – {i.profesional}
                              </strong>
                              <div style={{ marginTop: "8px" }}>
                                <div>📦 Insumo: <strong>{i.insumo}</strong></div>
                                <div>🏷️ Tipo: {i.tipoinsumo} | Cantidad: {i.cantidad}</div>
                                {i.obs && <div>📝 Observación: {i.obs}</div>}
                              </div>
                            </div>

                            {puedeEditar && (
                              <Button
                                size="sm"
                                variant="warning"
                                onClick={() => {
                                  setRegistroEditando(i);
                                  setTipoRegistro("insumo");
                                  setShowEditModal(true);
                                }}
                                title="Editar insumo"
                              >
                                <PencilSquare size={18} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Tab>

            {/* TAB SIGNOS VITALES */}
            <Tab eventKey="signos" title={`Signos Vitales (${signosFiltrados.length})`}>
              {signosFiltrados.length === 0 ? (
                <div className="alert alert-info">No hay signos vitales para mostrar</div>
              ) : (
                <Table bordered hover responsive>
                  <tbody>
                    {signosFiltrados.map((s, k) => (
                      <tr key={k}>
                        <td>
                          <div className="d-flex justify-content-between gap-3">
                            <div style={{ flex: 1 }}>
                              <strong style={{ color: "#e74c3c" }}>
                                {s.fechahora} – {s.profesional}
                              </strong>
                              <div style={{ marginTop: "8px" }}>
                                <div>🌡️ Temperatura: {s.temperatura} °C | 💉 PA: {s.pa}</div>
                                <div>❤️ FC: {s.fc} | 🫁 FR: {s.fr} | 🩺 SatO₂: {s.sato2}</div>
                                {s.hgt && <div>🩸 HGT: {s.hgt}</div>}
                              </div>
                            </div>

                            {puedeEditar && (
                              <Button
                                size="sm"
                                variant="warning"
                                onClick={() => {
                                  setRegistroEditando(s);
                                  setTipoRegistro("signos");
                                  setShowEditModal(true);
                                }}
                                title="Editar signos vitales"
                              >
                                <PencilSquare size={18} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Tab>
          </Tabs>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onCloseModal}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL EDICIÓN */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar Registro</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {tipoRegistro === "evolucion" && (
            <>
              <label className="form-label">
                Evolución ID: {registroEditando?.idevolucion}
              </label>
              <textarea
                className="form-control"
                rows={8}
                value={registroEditando?.texto || ""}
                onChange={(e) =>
                  setRegistroEditando({
                    ...registroEditando,
                    texto: e.target.value,
                  })
                }
              />
            </>
          )}

          {tipoRegistro === "medicacion" && (
            <>
              <label className="form-label">
                Medicación ID: {registroEditando?.idmedicacion}
              </label>
              
              <label className="form-label mt-3">Cantidad</label>
              <input
                type="number"
                className="form-control"
                value={registroEditando?.cantidad || ""}
                onChange={(e) =>
                  setRegistroEditando({
                    ...registroEditando,
                    cantidad: e.target.value,
                  })
                }
              />

              <label className="form-label mt-3">Tipo</label>
              <input
                type="text"
                className="form-control"
                value={registroEditando?.tipo || ""}
                onChange={(e) =>
                  setRegistroEditando({
                    ...registroEditando,
                    tipo: e.target.value,
                  })
                }
              />
            </>
          )}

          {tipoRegistro === "insumo" && (
            <>
              <label className="form-label">
                Insumo ID: {registroEditando?.idinsumo}
              </label>
              
              <label className="form-label mt-3">Cantidad</label>
              <input
                type="number"
                className="form-control"
                value={registroEditando?.cantidad || ""}
                onChange={(e) =>
                  setRegistroEditando({
                    ...registroEditando,
                    cantidad: e.target.value,
                  })
                }
              />

              <label className="form-label mt-3">Observación</label>
              <textarea
                className="form-control"
                rows={3}
                value={registroEditando?.obs || ""}
                onChange={(e) =>
                  setRegistroEditando({
                    ...registroEditando,
                    obs: e.target.value,
                  })
                }
              />
            </>
          )}

          {tipoRegistro === "signos" && (
            <>
              <label className="form-label">
                Signos Vitales ID: {registroEditando?.id}
              </label>
              
              <div className="row mt-3">
                <div className="col-6">
                  <label className="form-label">Temperatura (°C)</label>
                  <input
                    type="number"
                    step="0.1"
                    className="form-control"
                    value={registroEditando?.temperatura || ""}
                    onChange={(e) =>
                      setRegistroEditando({
                        ...registroEditando,
                        temperatura: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="col-6">
                  <label className="form-label">Presión Arterial</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="120/80"
                    value={registroEditando?.pa || ""}
                    onChange={(e) =>
                      setRegistroEditando({
                        ...registroEditando,
                        pa: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-4">
                  <label className="form-label">FC</label>
                  <input
                    type="number"
                    className="form-control"
                    value={registroEditando?.fc || ""}
                    onChange={(e) =>
                      setRegistroEditando({
                        ...registroEditando,
                        fc: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="col-4">
                  <label className="form-label">FR</label>
                  <input
                    type="number"
                    className="form-control"
                    value={registroEditando?.fr || ""}
                    onChange={(e) =>
                      setRegistroEditando({
                        ...registroEditando,
                        fr: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="col-4">
                  <label className="form-label">SatO₂ (%)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={registroEditando?.sato2 || ""}
                    onChange={(e) =>
                      setRegistroEditando({
                        ...registroEditando,
                        sato2: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <label className="form-label mt-3">HGT (opcional)</label>
              <input
                type="number"
                className="form-control"
                value={registroEditando?.hgt || ""}
                onChange={(e) =>
                  setRegistroEditando({
                    ...registroEditando,
                    hgt: e.target.value,
                  })
                }
              />
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={guardarEdicion}>
            💾 Guardar cambios
          </Button>
        </Modal.Footer>
      </Modal>
      
    </>
  );
};

export default ModalHojaEnfermeria;